// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    requestCustomQuoteForWishList as requestCustomQuoteApi,
    requestQuoteForWishList as requestQuoteApi,
    RequestQuoteFromWishListApiParameter,
} from "@bg/App/Services/WishListService";
import isApiError from "@insite/client-framework/Common/isApiError";
import { createHandlerChainRunner, HandlerWithResult } from "@insite/client-framework/HandlerCreator";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";

export interface UpdateWishListResult {
    errorMessage?: string;
    customQuoteId?: string;
}

type HandlerType = HandlerWithResult<
    {
        apiParameter: RequestQuoteFromWishListApiParameter;
        onSuccess?: () => void;
        onError?: (errorMessage: string) => void;
        isCustomQuote?: boolean;
    },
    UpdateWishListResult
>;

export const DispatchBeginRequestQuote: HandlerType = props => {
    const { wishlistId } = props.parameter.apiParameter;
    if (!wishlistId) {
        return;
    }
    props.dispatch({
        type: "commonActionSetter/SetAction",
        payload: {
            name: "isLoading",
            value: true,
        },
    } as any);
};

export const CheckParametersForGroups: HandlerType = props => {
    const { wishlistId, groupsProductsInfo } = props.parameter.apiParameter;
    if (!wishlistId) {
        return;
    }
    if (!groupsProductsInfo || Object.keys(groupsProductsInfo).length === 0) {
        delete props.parameter.apiParameter.groupsProductsInfo;
    }
};

export const CallRequestCustomQuoteApi: HandlerType = async props => {
    if (!props.parameter.isCustomQuote) {
        return;
    }
    try {
        props.result = await requestCustomQuoteApi(props.parameter.apiParameter);
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            props.result.errorMessage = error.errorJson.message;
            return;
        }
        throw error;
    }
};

export const CallRequestQuoteApi: HandlerType = async props => {
    if (props.parameter.isCustomQuote) {
        return;
    }
    try {
        props.result = await requestQuoteApi(props.parameter.apiParameter);
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            props.result.errorMessage = error.errorJson.message;
            return;
        }
        throw error;
    }
};

export const RedirectIfSuccess: HandlerType = props => {
    if (props.result.errorMessage) {
        return;
    }

    props.dispatch({
        type: "commonActionSetter/SetAction",
        payload: {
            name: "isLoading",
            value: false,
        },
    } as any);

    const requestQuotePageUrl = getPageLinkByPageType(props.getState(), "RfqRequestQuotePage")?.url;
    const requestCustomQuotePageUrl = getPageLinkByPageType(props.getState(), "MyCustomQuoteDetailsPage")?.url;
    if (!props.parameter.isCustomQuote && requestQuotePageUrl && !props.result.errorMessage) {
        window.location.href = requestQuotePageUrl;
    } else if (props.parameter.isCustomQuote && requestCustomQuotePageUrl && !props.result.errorMessage) {
        window.location.href = `${requestCustomQuotePageUrl}?id=${props.result.customQuoteId}`;
    }
};

export const chain = [
    DispatchBeginRequestQuote,
    CheckParametersForGroups,
    CallRequestCustomQuoteApi,
    CallRequestQuoteApi,
    RedirectIfSuccess,
];

const requestWishlistQuote = createHandlerChainRunner(chain, "RequestWishlistQuote");
export default requestWishlistQuote;
